import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { api } from "../../api";
import Modal from "../common/Modal";
import "./BookingTimeSelector.css";
import { formatArrivalTime } from "../../utils/formatArrivalTime";


const TIME_PERIODS = [
  { key: "morning", label: "Morning", range: "7am - 12pm" },
  { key: "afternoon", label: "Afternoon", range: "12pm - 5pm" },
  { key: "evening", label: "Evening", range: "5pm - 8pm" },
];

const formatDateLabel = (value) => {
  if (!value) {
    return moment().format("dddd, MMMM Do YYYY");
  }

  if (value === "Today") {
    return moment().format("dddd, MMMM Do YYYY");
  }

  if (value === "Tomorrow") {
    return moment().add(1, "day").format("dddd, MMMM Do YYYY");
  }

  return value;
};

const getTimeFormat = (timeKey = "") =>
  timeKey.toLowerCase().includes("am") || timeKey.toLowerCase().includes("pm")
    ? "h:mm A"
    : "HH:mm";

const getPeriodKeyFromTime = (timeKey) => {
  const format = getTimeFormat(timeKey);
  const parsed = moment(timeKey, format);
  const hour = parsed.hour();

  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
};

const normalizeTimeSlots = (rawSlots) => {
  const grouped = {
    morning: [],
    afternoon: [],
    evening: [],
  };

  if (!rawSlots) {
    return grouped;
  }

  if (Array.isArray(rawSlots)) {
    rawSlots.forEach((slot) => {
      // Only include available slots
      if (!slot.available) return;
      const period = getPeriodKeyFromTime(slot.timeKey);
      grouped[period].push(slot);
    });
    return grouped;
  }

  Object.entries(rawSlots).forEach(([key, slots]) => {
    const lowerKey = key.toLowerCase();
    if (grouped[lowerKey]) {
      // Filter out unavailable slots
      grouped[lowerKey] = (slots || []).filter((slot) => slot.available);
    }
  });

  return grouped;
};

const collectAllSlots = (groupedSlots) => [
  ...(groupedSlots?.morning || []),
  ...(groupedSlots?.afternoon || []),
  ...(groupedSlots?.evening || []),
];

const BookingTimeSelector = ({
  isOpen,
  onClose,
  onTimeSelect,
  location,
  currentBookingTime,
}) => {
  const [selectedDate, setSelectedDate] = useState(formatDateLabel("Today"));
  const [selectedDateLabel, setSelectedDateLabel] = useState("Today");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [activeTimeTab, setActiveTimeTab] = useState(null);
  const [timeSlots, setTimeSlots] = useState(null);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [displayedMonth, setDisplayedMonth] = useState(moment());

  const autoOpenedRef = useRef(false);
  const initialTimeKeyRef = useRef(null);

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal helper functions
  const showAlertModal = (title, message, type = "warning") => {
    setAlertModal({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeAlertModal = () => {
    setAlertModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Initialize with current booking time
  useEffect(() => {
    if (currentBookingTime && location) {
      const bookingMoment = moment(currentBookingTime);
      setSelectedDate(bookingMoment.format("dddd, MMMM Do YYYY"));
      setSelectedDateLabel(
        bookingMoment.isSame(moment(), "day")
          ? "Today"
          : bookingMoment.isSame(moment().add(1, "day"), "day")
          ? "Tomorrow"
          : bookingMoment.format("dddd Do MMM")
      );
      initialTimeKeyRef.current = bookingMoment.format("HH:mm");
      setSelectedTimeSlot(null);
      autoOpenedRef.current = false;
    }
  }, [currentBookingTime, location]);

  // Fetch time slots when date or location changes
  useEffect(() => {
    if (selectedDate && location && isOpen) {
      fetchTimeSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, location, isOpen]);

  const fetchTimeSlots = async () => {
    if (!location || !selectedDate) return;

    setIsLoadingTimeSlots(true);
    try {
      const dateForApi = moment(selectedDate, [
        "dddd, MMMM Do YYYY",
        "dddd Do MMMM",
      ]).format("YYYY-MM-DD");

      const response = await api(
        `booking-flow/timeslots/${location._id}?date=${dateForApi}`,
        "GET"
      );

      if (response.data.success) {
        const normalized = normalizeTimeSlots(response.data.timeSlots);
        setTimeSlots(normalized);

        if (initialTimeKeyRef.current) {
          const allSlots = collectAllSlots(normalized);
          const matchingSlot = allSlots.find(
            (slot) => slot.timeKey === initialTimeKeyRef.current
          );
          if (matchingSlot) {
            setSelectedTimeSlot(matchingSlot);
          }
        }
      } else {
        setTimeSlots(normalizeTimeSlots(null));
      }
    } catch (error) {
      console.error("Error fetching time slots:", error);
      setTimeSlots(normalizeTimeSlots(null));
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  const handleDateChange = (dateValue) => {
    if (dateValue === "Pick a date") {
      setIsCalendarModalOpen(true);
      return;
    }

    setSelectedDate(formatDateLabel(dateValue));
    setSelectedDateLabel(dateValue);
    setSelectedTimeSlot(null);
    setActiveTimeTab(null);
    initialTimeKeyRef.current = null;
    autoOpenedRef.current = false;
  };

  const handleTimeSlotSelect = (slot) => {
    // Validate 1 hour buffer for concierge bookings (ONLY for today - future dates have no buffer requirement)
    if (selectedDate === "Today" || selectedDate === moment().format("dddd, MMMM Do YYYY")) {
      // Only apply 1-hour buffer when booking for today
      const now = moment();
      const minimumBookingTime = now.clone().add(1, "hour");
      
      // Parse the selected time slot
      const [startTime] = (slot.timeRange || slot.timeKey).split(" - ");
      const slotMoment = moment(startTime, "h:mm A");
      
      // Set the date to today
      slotMoment.year(now.year());
      slotMoment.month(now.month());
      slotMoment.date(now.date());
      
      // Check if slot is less than 1 hour away
      if (slotMoment.isBefore(minimumBookingTime)) {
        showAlertModal(
          "Booking Too Soon",
          "Sorry, bookings must be made at least 1 hour in advance. Please select a time slot that is at least 1 hour from now.",
          "warning"
        );
        return;
      }
    }
    
    setSelectedTimeSlot(slot);
    setActiveTimeTab(null);
    initialTimeKeyRef.current = slot.timeKey;
    autoOpenedRef.current = true;
  };

  const handleTimeTabChange = (tabKey) => {
    if (activeTimeTab === tabKey) {
      setActiveTimeTab(null);
    } else {
      setActiveTimeTab(tabKey);
    }
  };

  const handleCalendarDateSelect = (date) => {
    const formattedDate = formatDateLabel(date.format("dddd, MMMM Do YYYY"));
    setSelectedDate(formattedDate);
    setSelectedDateLabel(date.format("dddd Do MMM"));
    setIsCalendarModalOpen(false);
    setSelectedTimeSlot(null);
    setActiveTimeTab(null);
    initialTimeKeyRef.current = null;
    autoOpenedRef.current = false;
  };

  useEffect(() => {
    if (selectedTimeSlot && timeSlots && !autoOpenedRef.current) {
      const periodKey = getPeriodKeyFromTime(selectedTimeSlot.timeKey);
      setActiveTimeTab(periodKey);
      autoOpenedRef.current = true;
    }
  }, [selectedTimeSlot, timeSlots]);

  const handleConfirm = () => {
    if (!selectedDate || !selectedTimeSlot) {
      showAlertModal(
        "Selection Required",
        "Please select both a date and time before confirming.",
        "warning"
      );
      return;
    }

    const normalizedDate = formatDateLabel(selectedDate);
    const timeFormat = getTimeFormat(selectedTimeSlot.timeKey);
    const selectedMoment = moment(
      `${normalizedDate} ${selectedTimeSlot.timeKey}`,
      [`dddd, MMMM Do YYYY ${timeFormat}`, `dddd Do MMMM ${timeFormat}`]
    );

    if (!selectedMoment.isValid()) {
      showAlertModal(
        "Invalid Date/Time",
        "Unable to parse selected date/time. Please try again.",
        "error"
      );
      return;
    }

    const formattedTime = selectedMoment.format("YYYY-MM-DD HH:mm");

    onTimeSelect(formattedTime);
    onClose();
  };

  const renderDateButtons = () => {
    const today = moment();
    const tomorrow = moment().add(1, "day");
    const dayAfterTomorrow = moment().add(2, "day");
    const selectedMoment = moment(selectedDate, [
      "dddd, MMMM Do YYYY",
      "dddd Do MMMM",
    ]);

    return (
      <div className="date-buttons">
        <button
          className={`date-btn ${selectedMoment.isSame(today, "day") ? "active" : ""}`}
          onClick={() => handleDateChange("Today")}
        >
          Today
        </button>
        <button
          className={`date-btn ${selectedMoment.isSame(tomorrow, "day") ? "active" : ""}`}
          onClick={() => handleDateChange("Tomorrow")}
        >
          Tomorrow
        </button>
        <button
          className={`date-btn ${
            selectedMoment.isSame(dayAfterTomorrow, "day") ? "active" : ""
          }`}
          onClick={() =>
            handleDateChange(dayAfterTomorrow.format("dddd, MMMM Do YYYY"))
          }
        >
          {dayAfterTomorrow.format("dddd Do")}
        </button>
        <button className="date-btn" onClick={() => handleDateChange("Pick a date")}>
          {selectedDateLabel !== "Today" && selectedDateLabel !== "Tomorrow"
            ? selectedDateLabel
            : "Pick a date"}
        </button>
      </div>
    );
  };

  const renderTimeTabs = () => {
    if (!timeSlots || isLoadingTimeSlots) {
      return (
        <div className="time-loading">
          {isLoadingTimeSlots
            ? "Loading available times..."
            : "Select a date first"}
        </div>
      );
    }

    // Filter out evening period if there are no time slots available
    const filteredPeriods = TIME_PERIODS.filter((period) => {
      if (period.key === "evening") {
        // Only show evening if there are available time slots
        return timeSlots.evening && timeSlots.evening.length > 0;
      }
      return true; // Always show morning and afternoon
    });

    return (
      <div className="time-tabs">
        {filteredPeriods.map((period) => {
          const slots = timeSlots?.[period.key] || [];
          const isActive = activeTimeTab === period.key;

          return (
            <div
              key={period.key}
              className={`time-tab-section ${isActive ? "active" : ""}`}
            >
              <div
                className={`time-tab ${isActive ? "active" : ""}`}
                onClick={() => handleTimeTabChange(period.key)}
              >
                <div className="time-tab-header">
                  <span className="time-tab-label">{period.label}</span>
                  <span className="time-tab-range">{period.range}</span>
                </div>
                <div className="time-tab-indicator">{isActive ? "−" : "+"}</div>
              </div>

              {isActive && (
                <div className="time-slots">
                  {slots.filter((slot) => slot.available).length > 0 ? (
                    <div className="time-slots-container">
                      {slots
                        .filter((slot) => slot.available) // Only show available slots
                        .map((slot, index) => (
                          <div
                            key={`${slot.timeKey}-${index}`}
                            className={`time-slot ${
                              selectedTimeSlot?.timeKey === slot.timeKey
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => handleTimeSlotSelect(slot)}
                          >
                            <span className="time-range">
                              {formatArrivalTime(slot)}
                            </span>
                            <div className="time-slot-indicator">
                              {selectedTimeSlot?.timeKey === slot.timeKey ? "✓" : ""}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="no-slots">
                      No available times for this period
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCalendarModal = () => {
    if (!isCalendarModalOpen) return null;

    const today = moment();
    const calendarDays = [];
    const startOfMonth = moment(displayedMonth).startOf("month");
    const endOfMonth = moment(displayedMonth).endOf("month");
    const startDate = startOfMonth.clone().startOf("week");
    const endDate = endOfMonth.clone().endOf("week");

    let currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate)) {
      calendarDays.push(currentDate.clone());
      currentDate.add(1, "day");
    }

    return (
      <div className="calendar-modal-overlay">
        <div className="calendar-modal">
          <div className="calendar-header">
            <button
              onClick={() =>
                setDisplayedMonth(moment(displayedMonth).subtract(1, "month"))
              }
            >
              ←
            </button>
            <h3>{displayedMonth.format("MMMM YYYY")}</h3>
            <button
              onClick={() =>
                setDisplayedMonth(moment(displayedMonth).add(1, "month"))
              }
            >
              →
            </button>
          </div>
          <div className="calendar-grid">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
              (day) => (
                <div key={day} className="calendar-day-header">
                  {day}
                </div>
              )
            )}
            {calendarDays.map((date, index) => (
              <button
                key={index}
                className={`calendar-day ${
                  date.isSame(today, "day") ? "today" : ""
                } ${
                  !date.isSame(displayedMonth, "month") ? "other-month" : ""
                }`}
                onClick={() => handleCalendarDateSelect(date)}
                disabled={date.isBefore(today, "day")}
              >
                {date.format("D")}
              </button>
            ))}
          </div>
          <button
            className="calendar-close-btn"
            onClick={() => setIsCalendarModalOpen(false)}
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="booking-time-selector-overlay">
      <div className="booking-time-selector">
        <div className="selector-header">
          <h3>Select New Date & Time</h3>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="selector-content">
          {/* Date Selection */}
          <div className="date-selection">
            <h4>Select Date</h4>
            {renderDateButtons()}
          </div>

          {/* Time Selection */}
          <div className="time-selection">
            <h4>Select Time</h4>
            {renderTimeTabs()}
            {selectedTimeSlot && (
              <div className="selected-time-summary">
                <span className="selected-time-label">Selected time</span>
                <span className="selected-time-value">
                  {formatArrivalTime(selectedTimeSlot)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="selector-actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="confirm-btn"
            onClick={handleConfirm}
            disabled={!selectedDate || !selectedTimeSlot}
          >
            Update Booking
          </button>
        </div>
      </div>

      {/* Calendar Modal */}
      {renderCalendarModal()}

      {/* Alert Modal */}
      <Modal
        isOpen={alertModal.isOpen}
        onClose={closeAlertModal}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
};

export default BookingTimeSelector;
